import React, { useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { getBurnoutRiskLevel } from '../../utils/dashboardSharedUtils';
import { corporateGetStaffSessions } from '../api';

function calculateSessionActivity(deptId, employees) {
  const deptEmployees = (employees || []).filter(emp => emp.department_id === deptId);
  if (deptEmployees.length === 0) return { sessionsThisWeek: 0, participation: 0 };

  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

  const employeesWithSessions = deptEmployees.filter(emp => {
    if (!emp.session_date) return false;
    const sessionDate = new Date(emp.session_date);
    return sessionDate >= oneWeekAgo;
  });

  const sessionsThisWeek = employeesWithSessions.length;
  const participation = deptEmployees.length > 0
    ? Math.round((sessionsThisWeek / deptEmployees.length) * 100)
    : 0;

  return { sessionsThisWeek, participation };
}

function getTopEmotion(dept) {
  const hasData = dept.has_data !== false && dept.avgEmotionScore !== null && dept.avgEmotionScore !== undefined;
  if (!hasData) {
    return { label: 'No Data', icon: '📊', color: '#6b7280' };
  }
  const emotion = dept.avgEmotionScore || 0;
  if (emotion >= 60) return { label: 'Positive', icon: '😊', color: '#10b981' };
  if (emotion >= 40) return { label: 'Neutral', icon: '😐', color: '#6b7280' };
  return { label: 'Negative', icon: '😔', color: '#ef4444' };
}

function generateMiniChartData(dept) {
  const baseEmotion = dept.avgEmotionScore || 50;
  const baseBurnout = dept.burnoutPercentage || 50;
  const baseEngagement = dept.avgEngagementScore || 50;

  return {
    emotion: [
      Math.max(0, Math.min(100, baseEmotion - 10)),
      Math.max(0, Math.min(100, baseEmotion - 5)),
      baseEmotion,
      Math.max(0, Math.min(100, baseEmotion + 5)),
      Math.max(0, Math.min(100, baseEmotion + 10))
    ],
    burnout: [
      Math.max(0, Math.min(100, baseBurnout - 10)),
      Math.max(0, Math.min(100, baseBurnout - 5)),
      baseBurnout,
      Math.max(0, Math.min(100, baseBurnout + 5)),
      Math.max(0, Math.min(100, baseBurnout + 10))
    ],
    engagement: [
      Math.max(0, Math.min(100, baseEngagement - 10)),
      Math.max(0, Math.min(100, baseEngagement - 5)),
      baseEngagement,
      Math.max(0, Math.min(100, baseEngagement + 5)),
      Math.max(0, Math.min(100, baseEngagement + 10))
    ]
  };
}

function calculateDepartmentPeriodMetrics(deptId, deptViewMode, departmentSessions, employees) {
  const deptEmployees = (employees || []).filter(emp => emp.department_id === deptId);
  if (deptEmployees.length === 0) return null;

  const now = new Date();
  let periodStart, periodEnd;

  if (deptViewMode === 'weekly') {
    periodEnd = new Date(now);
    periodEnd.setHours(23, 59, 59, 999);
    periodStart = new Date(now);
    periodStart.setDate(periodStart.getDate() - 7);
    periodStart.setHours(0, 0, 0, 0);
  } else {
    periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
    periodStart.setHours(0, 0, 0, 0);
    periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
  }

  const deptSessions = departmentSessions[deptId] || [];
  const periodSessions = deptSessions.filter(session => {
    if (!session.session_date) return false;
    const sessionDate = new Date(session.session_date);
    return sessionDate >= periodStart && sessionDate <= periodEnd;
  });

  const participatingStaffIds = [...new Set(periodSessions.map(s => s.staff_id))];
  const participatingEmployees = deptEmployees.filter(emp => participatingStaffIds.includes(emp.id));
  const participationRate = deptEmployees.length > 0
    ? (participatingEmployees.length / deptEmployees.length) * 100
    : 0;

  const validSessions = periodSessions.filter(s =>
    s.emotion_score !== null && s.emotion_score !== undefined &&
    s.motivation_score !== null && s.motivation_score !== undefined
  );

  const avgMetrics = {
    emotion: validSessions.length > 0
      ? validSessions.reduce((sum, s) => sum + (parseFloat(s.emotion_score) || 0), 0) / validSessions.length : 0,
    motivation: validSessions.length > 0
      ? validSessions.reduce((sum, s) => sum + (parseFloat(s.motivation_score) || 0), 0) / validSessions.length : 0,
    burnout: validSessions.length > 0
      ? validSessions.reduce((sum, s) => sum + (parseFloat(s.burnout_score) || 0), 0) / validSessions.length : 0,
    engagement: validSessions.length > 0
      ? validSessions.reduce((sum, s) => sum + (parseFloat(s.engagement_score) || 0), 0) / validSessions.length : 0,
    stress: validSessions.length > 0
      ? validSessions.reduce((sum, s) => sum + (parseFloat(s.stress_score) || 0), 0) / validSessions.length : 0,
    wellbeing: validSessions.length > 0
      ? validSessions.reduce((sum, s) => sum + (parseFloat(s.wellbeing_score) || 0), 0) / validSessions.length : 0
  };

  const employeeLatestSessions = {};
  periodSessions.forEach(session => {
    const empId = session.staff_id;
    if (!employeeLatestSessions[empId] ||
      new Date(session.session_date) > new Date(employeeLatestSessions[empId].session_date)) {
      employeeLatestSessions[empId] = session;
    }
  });

  const employeeRiskScores = Object.values(employeeLatestSessions).map(session => parseFloat(session.burnout_score) || 0);
  const highRisk = employeeRiskScores.filter(score => score >= 70).length;
  const mediumRisk = employeeRiskScores.filter(score => score >= 50 && score < 70).length;
  const lowRisk = employeeRiskScores.filter(score => score < 50).length;
  const burnoutDistribution = {
    high: employeeRiskScores.length > 0 ? (highRisk / employeeRiskScores.length) * 100 : 0,
    medium: employeeRiskScores.length > 0 ? (mediumRisk / employeeRiskScores.length) * 100 : 0,
    low: employeeRiskScores.length > 0 ? (lowRisk / employeeRiskScores.length) * 100 : 0
  };

  const flags = [];
  Object.values(employeeLatestSessions).forEach(session => {
    const emp = deptEmployees.find(e => e.id === session.staff_id);
    if (!emp) return;
    const burnoutScore = parseFloat(session.burnout_score) || 0;
    const stressScore = parseFloat(session.stress_score) || 0;
    const motivationScore = parseFloat(session.motivation_score) || 0;
    if (burnoutScore >= 70) flags.push({ type: 'High Burnout', staff: emp.name, value: burnoutScore.toFixed(1) });
    if (stressScore > 75) flags.push({ type: 'High Stress', staff: emp.name, value: stressScore.toFixed(1) });
    if (motivationScore < 30) flags.push({ type: 'Low Motivation', staff: emp.name, value: motivationScore.toFixed(1) });
  });
  const topFlags = flags.sort((a, b) => parseFloat(b.value) - parseFloat(a.value)).slice(0, 5);

  const generateEngagementTrend = () => {
    if (deptViewMode === 'weekly') {
      const dailyEngagement = [];
      for (let i = 6; i >= 0; i--) {
        const dayStart = new Date(periodEnd);
        dayStart.setDate(dayStart.getDate() - i);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(dayStart);
        dayEnd.setHours(23, 59, 59, 999);
        const daySessions = periodSessions.filter(s => {
          if (!s.session_date) return false;
          const sessionDate = new Date(s.session_date);
          return sessionDate >= dayStart && sessionDate <= dayEnd;
        });
        const dayAvg = daySessions.length > 0
          ? daySessions.reduce((sum, s) => sum + (parseFloat(s.engagement_score) || 0), 0) / daySessions.length
          : null;
        dailyEngagement.push({
          period: dayStart.toLocaleDateString('en-US', { weekday: 'short' }),
          engagement: dayAvg !== null ? dayAvg : 0,
          hasData: dayAvg !== null
        });
      }
      return dailyEngagement;
    } else {
      const weeklyEngagement = [];
      const monthStart = new Date(periodStart);
      let currentWeekStart = new Date(monthStart);
      while (currentWeekStart <= periodEnd) {
        let weekEnd = new Date(currentWeekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        if (weekEnd > periodEnd) weekEnd = new Date(periodEnd);
        const weekSessions = periodSessions.filter(s => {
          if (!s.session_date) return false;
          const sessionDate = new Date(s.session_date);
          return sessionDate >= currentWeekStart && sessionDate <= weekEnd;
        });
        const weekAvg = weekSessions.length > 0
          ? weekSessions.reduce((sum, s) => sum + (parseFloat(s.engagement_score) || 0), 0) / weekSessions.length
          : null;
        weeklyEngagement.push({
          period: `Week ${weeklyEngagement.length + 1}`,
          engagement: weekAvg !== null ? weekAvg : 0,
          hasData: weekAvg !== null
        });
        currentWeekStart.setDate(currentWeekStart.getDate() + 7);
      }
      return weeklyEngagement;
    }
  };

  const engagementTrendData = generateEngagementTrend();

  return {
    periodStart,
    periodEnd,
    totalEmployees: deptEmployees.length,
    participatingEmployees: participatingEmployees.length,
    participationRate,
    avgMetrics,
    burnoutDistribution,
    topFlags,
    engagementTrendData,
    totalSessions: periodSessions.length
  };
}

export default function DepartmentDetailModal({
  department,
  employees,
  departmentSessions,
  loadingDepartmentSessions,
  deptViewMode,
  onViewModeChange,
  onClose,
  onLoadSessions,
  t
}) {
  const dept = department;

  const sessionActivity = useMemo(() => calculateSessionActivity(dept.id, employees), [dept.id, employees]);
  const topEmotion = useMemo(() => getTopEmotion(dept), [dept]);
  const burnoutRisk = useMemo(() => getBurnoutRiskLevel(dept.burnoutPercentage), [dept.burnoutPercentage]);
  const chartData = useMemo(() => generateMiniChartData(dept), [dept]);

  const showReport = deptViewMode !== 'overview';
  const deptReportData = useMemo(() => {
    if (!showReport) return null;
    return calculateDepartmentPeriodMetrics(dept.id, deptViewMode, departmentSessions, employees);
  }, [showReport, dept.id, deptViewMode, departmentSessions, employees]);

  const hasReportData = showReport && deptReportData && deptReportData.participatingEmployees > 0;

  const handleViewModeChange = (newMode) => {
    onViewModeChange(newMode);
    if (newMode !== 'overview' && !departmentSessions[dept.id]) {
      onLoadSessions(dept.id);
    }
  };

  if (showReport && loadingDepartmentSessions && !departmentSessions[dept.id]) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="department-report-modal department-detailed-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h2>{dept.name} - Department Overview</h2>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="modal-content department-report-modal-content">
            <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
              <p>Loading session data for {deptViewMode} report...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="department-report-modal department-detailed-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{dept.name} - Department Overview</h2>
          <div className="report-type-selector-container">
            <button
              className={`report-type-btn ${deptViewMode === 'weekly' ? 'active' : ''}`}
              onClick={() => handleViewModeChange(deptViewMode === 'weekly' ? 'overview' : 'weekly')}
            >
              📅 Weekly
            </button>
            <button
              className={`report-type-btn ${deptViewMode === 'monthly' ? 'active' : ''}`}
              onClick={() => handleViewModeChange(deptViewMode === 'monthly' ? 'overview' : 'monthly')}
            >
              📆 Monthly
            </button>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-content department-report-modal-content">
          <div className="department-report-body">
            {deptViewMode === 'weekly' && hasReportData && (
              <div className="dept-period-report-section" style={{ marginTop: '1rem' }}>
                <h3>📅 Weekly Report</h3>
                <div className="report-header-section">
                  <p className="report-period">
                    {deptReportData.periodStart.toLocaleDateString()} – {deptReportData.periodEnd.toLocaleDateString()}
                  </p>
                </div>
                <div className="dept-report-summary-grid">
                  <div className="dept-summary-card">
                    <h4>👥 Participation</h4>
                    <div className="summary-value-large">{deptReportData.participationRate.toFixed(1)}%</div>
                    <div className="summary-subtext">
                      {deptReportData.participatingEmployees} of {deptReportData.totalEmployees} employees
                    </div>
                  </div>
                  <div className="dept-summary-card">
                    <h4>🔥 Burnout Risk Distribution</h4>
                    <div className="burnout-distribution">
                      <div className="dist-item">
                        <span className="dist-label">High:</span>
                        <span className="dist-value high">{deptReportData.burnoutDistribution.high.toFixed(1)}%</span>
                      </div>
                      <div className="dist-item">
                        <span className="dist-label">Medium:</span>
                        <span className="dist-value medium">{deptReportData.burnoutDistribution.medium.toFixed(1)}%</span>
                      </div>
                      <div className="dist-item">
                        <span className="dist-label">Low:</span>
                        <span className="dist-value low">{deptReportData.burnoutDistribution.low.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                  <div className="dept-summary-card">
                    <h4>📊 Total Sessions</h4>
                    <div className="summary-value-large">{deptReportData.totalSessions || 0}</div>
                    <div className="summary-subtext">Sessions conducted in this period</div>
                  </div>
                </div>
                {deptReportData.topFlags.length > 0 && (
                  <div className="dept-flags-section">
                    <h4>⚠️ Top Flagged Risks</h4>
                    <div className="flags-table">
                      <div className="flags-table-header">
                        <span>Type</span>
                        <span>Staff Member</span>
                        <span>Value</span>
                      </div>
                      {deptReportData.topFlags.map((flag, idx) => (
                        <div key={idx} className="flags-table-row">
                          <span className={`flag-type ${flag.type.toLowerCase().replace(' ', '-')}`}>{flag.type}</span>
                          <span className="flag-staff">{flag.staff}</span>
                          <span className="flag-value">{flag.value}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {deptReportData.engagementTrendData && deptReportData.engagementTrendData.length > 0 && (
                  <div className="report-trend-chart">
                    <h4>Engagement Trend</h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={deptReportData.engagementTrendData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" />
                        <YAxis domain={[0, 100]} />
                        <Tooltip
                          formatter={(value, name) => {
                            const dataPoint = deptReportData.engagementTrendData.find(d => d.period === name);
                            if (value === 0 && (!dataPoint || !dataPoint.hasData)) {
                              return ['No data', 'Engagement'];
                            }
                            return [`${value.toFixed(1)}%`, 'Engagement'];
                          }}
                        />
                        <Area type="monotone" dataKey="engagement" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} name="Engagement" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}

            {deptViewMode === 'monthly' && hasReportData && (
              <div className="dept-period-report-section" style={{ marginTop: '1rem' }}>
                <h3>📆 Monthly Report</h3>
                <div className="report-header-section">
                  <p className="report-period">
                    {deptReportData.periodStart.toLocaleDateString()} – {deptReportData.periodEnd.toLocaleDateString()}
                  </p>
                </div>
                <div className="dept-report-summary-grid">
                  <div className="dept-summary-card">
                    <h4>👥 Participation</h4>
                    <div className="summary-value-large">{deptReportData.participationRate.toFixed(1)}%</div>
                    <div className="summary-subtext">
                      {deptReportData.participatingEmployees} of {deptReportData.totalEmployees} employees
                    </div>
                  </div>
                  <div className="dept-summary-card">
                    <h4>🔥 Burnout Risk Distribution</h4>
                    <div className="burnout-distribution">
                      <div className="dist-item">
                        <span className="dist-label">High:</span>
                        <span className="dist-value high">{deptReportData.burnoutDistribution.high.toFixed(1)}%</span>
                      </div>
                      <div className="dist-item">
                        <span className="dist-label">Medium:</span>
                        <span className="dist-value medium">{deptReportData.burnoutDistribution.medium.toFixed(1)}%</span>
                      </div>
                      <div className="dist-item">
                        <span className="dist-label">Low:</span>
                        <span className="dist-value low">{deptReportData.burnoutDistribution.low.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>
                  <div className="dept-summary-card">
                    <h4>📊 Total Sessions</h4>
                    <div className="summary-value-large">{deptReportData.totalSessions || 0}</div>
                    <div className="summary-subtext">Sessions conducted in this period</div>
                  </div>
                </div>
                {deptReportData.topFlags.length > 0 && (
                  <div className="dept-flags-section">
                    <h4>⚠️ Top Flagged Risks</h4>
                    <div className="flags-table">
                      <div className="flags-table-header">
                        <span>Type</span>
                        <span>Staff Member</span>
                        <span>Value</span>
                      </div>
                      {deptReportData.topFlags.map((flag, idx) => (
                        <div key={idx} className="flags-table-row">
                          <span className={`flag-type ${flag.type.toLowerCase().replace(' ', '-')}`}>{flag.type}</span>
                          <span className="flag-staff">{flag.staff}</span>
                          <span className="flag-value">{flag.value}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {deptReportData.engagementTrendData && deptReportData.engagementTrendData.length > 0 && (
                  <div className="report-trend-chart">
                    <h4>Engagement Trend</h4>
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={deptReportData.engagementTrendData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" />
                        <YAxis domain={[0, 100]} />
                        <Tooltip
                          formatter={(value, name) => {
                            const dataPoint = deptReportData.engagementTrendData.find(d => d.period === name);
                            if (value === 0 && (!dataPoint || !dataPoint.hasData)) {
                              return ['No data', 'Engagement'];
                            }
                            return [`${value.toFixed(1)}%`, 'Engagement'];
                          }}
                        />
                        <Area type="monotone" dataKey="engagement" stroke="#82ca9d" fill="#82ca9d" fillOpacity={0.6} name="Engagement" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}

            {deptViewMode !== 'overview' && !hasReportData && (
              <div className="report-no-data" style={{ marginTop: '1rem' }}>
                <p>Not enough data to generate {deptViewMode} report.</p>
                <p className="report-hint">Conduct sessions for department staff to generate report data.</p>
              </div>
            )}

            {deptViewMode === 'overview' && (
              <>
                <div className="dept-modal-header-section" style={{ marginTop: '1rem' }}>
                  <div className="dept-modal-header-left">
                    <h3 className="dept-modal-name">{dept.name}</h3>
                    <div className="dept-modal-employee-count">
                      <span className="dept-icon">👥</span>
                      <span className="dept-count-value">{dept.employeeCount ?? dept.staff_count ?? 0} employees</span>
                    </div>
                  </div>
                  <div className="dept-modal-header-right">
                    <div className="dept-modal-top-emotion">
                      <span className="top-emotion-label">Top Emotion:</span>
                      <span className="top-emotion-value" style={{ color: topEmotion.color }}>
                        {topEmotion.icon} {topEmotion.label}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="dept-metrics-grid">
                  <div className="dept-metric-card" title="Average emotion score across all employees (0-100)">
                    <div className="metric-icon">😊</div>
                    <div className="metric-content">
                      <div className="metric-label">Emotion</div>
                      <div className="metric-value">
                        {dept.avgEmotionScore !== null && dept.avgEmotionScore !== undefined ? dept.avgEmotionScore.toFixed(1) : 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className="dept-metric-card" title="Average motivation score across all employees (0-100)">
                    <div className="metric-icon">🚀</div>
                    <div className="metric-content">
                      <div className="metric-label">Motivation</div>
                      <div className="metric-value">
                        {dept.avgMotivationScore !== null && dept.avgMotivationScore !== undefined ? dept.avgMotivationScore.toFixed(1) : 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className="dept-metric-card burnout-metric" title={`Burnout risk level: ${burnoutRisk.label}`}>
                    <div className="metric-icon">🔥</div>
                    <div className="metric-content">
                      <div className="metric-label">Burnout Risk</div>
                      <div className="metric-value-with-badge">
                        <span className="metric-value">
                          {dept.burnoutPercentage !== null && dept.burnoutPercentage !== undefined ? `${dept.burnoutPercentage.toFixed(1)}%` : 'N/A'}
                        </span>
                        {dept.has_data && (
                          <span className={`burnout-badge ${burnoutRisk.level}`} style={{ backgroundColor: burnoutRisk.bgColor, color: burnoutRisk.color }}>
                            {burnoutRisk.label}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="dept-metric-card" title="Average engagement score across all employees (0-100)">
                    <div className="metric-icon">💼</div>
                    <div className="metric-content">
                      <div className="metric-label">Engagement</div>
                      <div className="metric-value">
                        {dept.avgEngagementScore !== null && dept.avgEngagementScore !== undefined ? dept.avgEngagementScore.toFixed(1) : 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className="dept-metric-card" title="Average stress index across all employees (0-100)">
                    <div className="metric-icon">😰</div>
                    <div className="metric-content">
                      <div className="metric-label">Stress</div>
                      <div className="metric-value">
                        {dept.avgStressScore !== null && dept.avgStressScore !== undefined ? dept.avgStressScore.toFixed(1) : 'N/A'}
                      </div>
                    </div>
                  </div>
                  <div className="dept-metric-card" title="Average wellbeing index across all employees (0-100)">
                    <div className="metric-icon">❤️</div>
                    <div className="metric-content">
                      <div className="metric-label">Wellbeing</div>
                      <div className="metric-value">
                        {dept.avgWellbeingScore !== null && dept.avgWellbeingScore !== undefined ? dept.avgWellbeingScore.toFixed(1) : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="dept-secondary-info">
                  <div className="dept-info-item" title="Sessions conducted this week and participation rate">
                    <span className="info-label">Sessions:</span>
                    <span className="info-value">
                      {sessionActivity.sessionsThisWeek} this week ({sessionActivity.participation}% participation)
                    </span>
                  </div>
                </div>

                <div className="dept-mini-charts">
                  <div className="mini-chart-item" title="Emotion trend (last 5 data points)">
                    <div className="mini-chart-label">Emotion</div>
                    <div className="mini-chart-sparkline">
                      {chartData.emotion.map((val, idx) => (
                        <div key={idx} className="sparkline-bar" style={{ height: `${val}%`, backgroundColor: val >= 60 ? '#10b981' : val >= 40 ? '#f59e0b' : '#ef4444' }} />
                      ))}
                    </div>
                  </div>
                  <div className="mini-chart-item" title="Burnout trend (last 5 data points)">
                    <div className="mini-chart-label">Burnout</div>
                    <div className="mini-chart-sparkline">
                      {chartData.burnout.map((val, idx) => (
                        <div key={idx} className="sparkline-bar" style={{ height: `${val}%`, backgroundColor: val >= 70 ? '#ef4444' : val >= 50 ? '#f59e0b' : '#10b981' }} />
                      ))}
                    </div>
                  </div>
                  <div className="mini-chart-item" title="Engagement trend (last 5 data points)">
                    <div className="mini-chart-label">Engagement</div>
                    <div className="mini-chart-sparkline">
                      {chartData.engagement.map((val, idx) => (
                        <div key={idx} className="sparkline-bar" style={{ height: `${val}%`, backgroundColor: val >= 70 ? '#10b981' : val >= 50 ? '#f59e0b' : '#ef4444' }} />
                      ))}
                    </div>
                  </div>
                </div>

                <div className="dept-legend">
                  <div className="legend-item">
                    <span className="legend-color positive"></span>
                    <span className="legend-text">Positive</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color neutral"></span>
                    <span className="legend-text">Neutral</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color negative"></span>
                    <span className="legend-text">Negative</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color low-risk"></span>
                    <span className="legend-text">Low Risk</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color medium-risk"></span>
                    <span className="legend-text">Medium Risk</span>
                  </div>
                  <div className="legend-item">
                    <span className="legend-color high-risk"></span>
                    <span className="legend-text">High Risk</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
